/**
 * useBusinessClosedDates Hook
 *
 * React hook for managing business closed dates with optimistic updates,
 * caching, and integration with the unified closure status service.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { BusinessClosedDate, DateClosureStatus, BusinessOpeningHour } from '@/types';
import {
  getClosedDatesForRange,
  addClosedDate,
  removeClosedDate,
  updateClosedDate,
  getDateClosureStatusSync,
  getShiftsForDate,
  getClosedDatesMap,
  getAutoCloseOnHolidaysSetting,
  updateAutoCloseOnHolidaysSetting
} from '@/lib/business/business-closed-dates-service';

// Holiday interface (simplified)
interface Holiday {
  date: string;
  name: string;
  is_non_working_day: boolean;
}

interface UseBusinessClosedDatesProps {
  businessId: string | null;
  startDate: Date;
  endDate: Date;
  openingHours?: BusinessOpeningHour[] | null;
  holidays?: Holiday[];
}

interface UseBusinessClosedDatesReturn {
  // Data
  closedDates: BusinessClosedDate[];
  closedDatesMap: Map<string, BusinessClosedDate>;
  autoCloseOnHolidays: boolean;

  // State
  loading: boolean;
  error: string | null;

  // Actions
  refresh: () => Promise<void>;
  addDate: (date: Date, reason?: string) => Promise<BusinessClosedDate>;
  removeDate: (id: string) => Promise<void>;
  updateReason: (id: string, reason: string) => Promise<void>;
  setAutoCloseOnHolidays: (autoClose: boolean) => Promise<void>;

  // Utilities
  isDateClosed: (date: Date) => DateClosureStatus;
  getClosedDatesForMonth: (month: Date) => BusinessClosedDate[];
  checkExistingShifts: (date: Date) => Promise<{ count: number; hasAssignedShifts: boolean }>;
}

export function useBusinessClosedDates({
  businessId,
  startDate,
  endDate,
  openingHours,
  holidays
}: UseBusinessClosedDatesProps): UseBusinessClosedDatesReturn {
  const [closedDates, setClosedDates] = useState<BusinessClosedDate[]>([]);
  const [autoCloseOnHolidays, setAutoCloseOnHolidaysState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch closed dates for the date range
  const fetchClosedDates = useCallback(async () => {
    if (!businessId) {
      setClosedDates([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [data, autoCloseSetting] = await Promise.all([
        getClosedDatesForRange(businessId, startDate, endDate),
        getAutoCloseOnHolidaysSetting(businessId)
      ]);
      setClosedDates(data);
      setAutoCloseOnHolidaysState(autoCloseSetting);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch closed dates';
      setError(errorMessage);
      console.error('Error fetching closed dates:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId, startDate, endDate]);

  // Initial fetch
  useEffect(() => {
    fetchClosedDates();
  }, [fetchClosedDates]);

  // Create a map for efficient lookups
  const closedDatesMap = useMemo(() => {
    return getClosedDatesMap(closedDates);
  }, [closedDates]);

  // Add a new closed date
  const addDate = useCallback(async (date: Date, reason?: string): Promise<BusinessClosedDate> => {
    if (!businessId) {
      throw new Error('Business ID is required');
    }

    try {
      const newClosedDate = await addClosedDate(businessId, date, reason);

      // Optimistic update
      setClosedDates(prev =>
        [...prev, newClosedDate].sort((a, b) =>
          a.closed_date.localeCompare(b.closed_date)
        )
      );

      return newClosedDate;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add closed date';
      setError(errorMessage);
      throw err;
    }
  }, [businessId]);

  // Remove a closed date
  const removeDateFn = useCallback(async (id: string): Promise<void> => {
    try {
      await removeClosedDate(id);

      // Optimistic update
      setClosedDates(prev => prev.filter(cd => cd.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove closed date';
      setError(errorMessage);
      throw err;
    }
  }, []);

  // Update closed date reason
  const updateReason = useCallback(async (id: string, reason: string): Promise<void> => {
    try {
      const updated = await updateClosedDate(id, reason);

      // Optimistic update
      setClosedDates(prev => prev.map(cd => cd.id === id ? updated : cd));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update closed date';
      setError(errorMessage);
      throw err;
    }
  }, []);

  // Update auto close on holidays setting
  const setAutoCloseOnHolidays = useCallback(async (autoClose: boolean): Promise<void> => {
    if (!businessId) {
      throw new Error('Business ID is required');
    }

    try {
      await updateAutoCloseOnHolidaysSetting(businessId, autoClose);
      setAutoCloseOnHolidaysState(autoClose);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update setting';
      setError(errorMessage);
      throw err;
    }
  }, [businessId]);

  // Check if a specific date is closed (using sync version with pre-fetched data)
  const isDateClosed = useCallback((date: Date): DateClosureStatus => {
    return getDateClosureStatusSync(date, openingHours, closedDates, {
      holidays,
      autoCloseOnHolidays
    });
  }, [openingHours, closedDates, holidays, autoCloseOnHolidays]);

  // Get closed dates for a specific month
  const getClosedDatesForMonth = useCallback((month: Date): BusinessClosedDate[] => {
    const year = month.getFullYear();
    const monthNum = month.getMonth();

    return closedDates.filter(cd => {
      const cdDate = new Date(cd.closed_date);
      return cdDate.getFullYear() === year && cdDate.getMonth() === monthNum;
    });
  }, [closedDates]);

  // Check existing shifts for a date (before marking as closed)
  const checkExistingShifts = useCallback(async (date: Date) => {
    if (!businessId) return { count: 0, hasAssignedShifts: false };
    return getShiftsForDate(businessId, date);
  }, [businessId]);

  return {
    // Data
    closedDates,
    closedDatesMap,
    autoCloseOnHolidays,

    // State
    loading,
    error,

    // Actions
    refresh: fetchClosedDates,
    addDate,
    removeDate: removeDateFn,
    updateReason,
    setAutoCloseOnHolidays,

    // Utilities
    isDateClosed,
    getClosedDatesForMonth,
    checkExistingShifts
  };
}

export default useBusinessClosedDates;
